'use client';

import { useQuery } from '@tanstack/react-query';

const STALE_THRESHOLD_MS = 60_000;

export interface TakeoverPriceInfo {
  priceUsd: number;
  totalSupply: string;
  marketCapUsd: number;
  updatedAt: number;
  isStale: boolean;
  isUnavailable: boolean;
  /** Snapshot price ~24h ago. `null` while the rolling history warms up. */
  priceUsd24hAgo: number | null;
  /** USD change vs 24h ago (live MC − historical MC). */
  marketCapChange24h: number | null;
  /** Percent change vs 24h ago (e.g. `-3.4` for −3.4%). */
  marketCapChangePercent24h: number | null;
}

interface TakeoverPriceApiResponse {
  priceUsd: number;
  totalSupply?: string;
  marketCapUsd?: number;
  updatedAt: number;
  priceUsd24hAgo?: number | null;
  marketCapChange24h?: number | null;
  marketCapChangePercent24h?: number | null;
}

/**
 * Live TAKEOVER/USD price polled every 15 seconds from onchain pools.
 * Also returns onchain totalSupply, the derived USD market cap, and
 * a 24h change ratio sourced from the rolling snapshot table.
 */
export function useTakeoverPrice(): TakeoverPriceInfo {
  const { data } = useQuery({
    queryKey: ['takeover-price-live'],
    queryFn: async () => {
      const res = await fetch('/api/takeover-price');
      if (!res.ok) throw new Error('price fetch failed');
      return res.json() as Promise<TakeoverPriceApiResponse>;
    },
    // `staleTime` must be >= `refetchInterval` or every new observer
    // that mounts during the `[staleTime, refetchInterval)` window
    // kicks an extra refetch — that's the "double-fire per 15s tick"
    // we measured on both branches. Matching them collapses burst
    // mounts (nav re-render, panel open) to one fetch per interval.
    staleTime: 15_000,
    refetchInterval: 15_000,
  });

  const priceUsd = data?.priceUsd ?? 0;
  const totalSupply = data?.totalSupply ?? '0';
  const marketCapUsd = data?.marketCapUsd ?? 0;
  const updatedAt = data?.updatedAt ?? 0;

  return {
    priceUsd,
    totalSupply,
    marketCapUsd,
    updatedAt,
    isStale: updatedAt > 0 && Date.now() - updatedAt > STALE_THRESHOLD_MS,
    isUnavailable: priceUsd === 0,
    priceUsd24hAgo: data?.priceUsd24hAgo ?? null,
    marketCapChange24h: data?.marketCapChange24h ?? null,
    marketCapChangePercent24h: data?.marketCapChangePercent24h ?? null,
  };
}
